import React, { useState, useEffect, useRef } from 'react';
import SmartBuyLogo from '../../images/SB.png';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { useSidebar } from '../../contexts/SidebarContext';

interface NavigationBarProps {
  currentScreen: string;
}

const NavigationBar: React.FC<NavigationBarProps> = ({ 
  currentScreen
}) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isMobile, isOpen, toggleSidebar } = useSidebar();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 transition-colors duration-300">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Left side - Logo */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Mobile Hamburger Menu Button */}
            {isMobile && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Toggle mobile menu"
              >
                <div className="w-6 h-6 flex flex-col justify-center items-center">
                  <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
                  <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
                </div>
              </button>
            )}
            
            {/* Logo */}
            <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/dashboard')}>
              <img 
                src={SmartBuyLogo} 
                alt="SmartBuy" 
                className="h-6 sm:h-8 w-auto"
              />
              <div className="hidden lg:block">
                <span className="text-lg font-semibold text-gray-800 dark:text-white transition-colors">Satın Alma & Talep Yönetimi</span>
              </div>
            </div>
          </div>

          {/* Right side - Page Title and Profile */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <h1 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white transition-colors truncate max-w-[150px] sm:max-w-none">
              {getPageTitle(currentScreen)}
            </h1>
            
            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center space-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <i className="fas fa-user text-gray-500 dark:text-gray-400 text-xs sm:text-sm"></i>
                </div>
                <span className="hidden sm:block text-sm font-medium">{user?.fullName}</span> {/* fullName */}
                <i className="fas fa-chevron-down text-xs"></i>
              </button>
              
              {showProfileDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 min-w-48">
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{user?.fullName}</p> {/* fullName */}
                    </div>
                    <button 
                      onClick={() => dispatch(logout())}
                      className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                    >
                      <i className="fas fa-sign-out-alt text-red-500"></i>
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Helper function to get page title based on current screen
const getPageTitle = (screen: string): string => {
  const titles: { [key: string]: string } = {
    'dashboard': 'Dashboard',
    'request-evaluation': 'Talep Değerlendirme',
    'create-request': 'Talep Oluştur',
    'request-tracking': 'Talep Takibi',
    'draft-quote': 'Teklif Talebi Hazırlama',
    'negotiation-quote': 'Müzakere Teklifi Hazırlama',
    'quote-evaluation': 'Teklif Değerlendirme',
    'quote-tracking': 'Teklif Mail Yönetimi',
    'order-invoice-comparison': 'Sipariş-Fatura Karşılaştırma',
    'quote-document': 'Teklif Belgesi',
    'purchase-order': 'Satın Alma Siparişi Hazırlama',
    'product-inquiry': 'Ürün Sorgulama',
    'request-info': 'Talep Durumu',
    'login': 'Giriş',
    // Eski route'lar için geriye dönük uyumluluk
    'main-menu': 'Ana Sayfa',
    'requests': 'Talep Değerlendirme',
    'quote-request': 'Teklif Talebi Hazırlama'
  };
  
  return titles[screen] || 'SmartBuy';
};

export default NavigationBar;
